import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { DownloadSimple, List, X } from "@phosphor-icons/react";
import { Logo } from "./Logo";
import { NAV_LINKS, MOBILE_EXTRA_LINKS } from "@/lib/site";
import useT, { localize, stripLang } from "@/i18n";

const linkCls = ({ isActive }) =>
  `tap inline-flex items-center px-4 text-sm transition-colors duration-200 ${isActive ? "text-[#7DDCFF]" : "text-slate-300 hover:text-white"}`;

const LangSwitch = ({ testId, className = "" }) => {
  const { lang, t } = useT();
  const { pathname } = useLocation();
  const other = lang === "hi" ? "en" : "hi";
  return (
    <Link to={localize(stripLang(pathname), other)} hrefLang={other === "hi" ? "hi-IN" : "en-IN"} lang={other === "hi" ? "hi" : "en"} aria-label={t.nav.switchLabel} className={`tap inline-flex items-center justify-center rounded-xl border px-3 text-sm text-slate-200 hover:border-[#00BFFF]/60 transition-colors ${className}`} style={{ borderColor: "rgba(255,255,255,0.12)" }} data-testid={testId}>
      {t.nav.switchTo}
    </Link>
  );
};

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { t, lp } = useT();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <header className="fixed top-0 inset-x-0 z-50 safe-top border-b" style={{ background: "rgba(5,11,20,0.78)", backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)", borderColor: "rgba(255,255,255,0.06)" }}>
      <div className="max-w-7xl mx-auto safe-x sm:px-8 h-16 flex items-center justify-between gap-4">
        <Logo />
        <nav aria-label="Primary" className="hidden md:flex items-center">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={lp(l.to)} className={linkCls} data-testid={`nav-${l.to.slice(1)}`}>{t.nav[l.key]}</NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <LangSwitch testId="lang-switch" className="hidden md:inline-flex" />
          <Link to={lp("/download")} className="btn-primary !min-h-[44px] !py-2 !px-5 text-sm hidden xs:inline-flex" data-testid="nav-get-apk">
            <DownloadSimple size={16} weight="bold" aria-hidden="true" /> {t.nav.getApk}
          </Link>
          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center rounded-xl border text-white"
            style={{ borderColor: "rgba(255,255,255,0.12)" }}
            aria-label={open ? t.nav.close : t.nav.open}
            aria-expanded={open}
            aria-controls="mobile-menu"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} aria-hidden="true" /> : <List size={22} aria-hidden="true" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            id="mobile-menu"
            aria-label="Mobile"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.2 }}
            className="md:hidden border-t safe-x pb-6 pt-2"
            style={{ background: "#050B14", borderColor: "rgba(255,255,255,0.06)", maxHeight: "calc(100dvh - 64px)", overflowY: "auto" }}
            data-testid="mobile-menu-panel"
          >
            <ul className="flex flex-col">
              {[{ to: "/", key: "home" }, ...NAV_LINKS, ...MOBILE_EXTRA_LINKS].map((l) => (
                <li key={l.to}>
                  <NavLink end to={lp(l.to)} className={({ isActive }) => `tap flex items-center py-3 text-base font-display border-b ${isActive ? "text-[#7DDCFF]" : "text-slate-200"}`} style={{ borderColor: "rgba(255,255,255,0.05)" }} data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`}>
                    {t.nav[l.key]}
                  </NavLink>
                </li>
              ))}
            </ul>
            <div className="flex gap-3 mt-5">
              <LangSwitch testId="mobile-lang-switch" className="shrink-0" />
              <Link to={lp("/download")} className="btn-primary flex-1" data-testid="mobile-get-apk">
                <DownloadSimple size={18} weight="bold" aria-hidden="true" /> {t.nav.getApk}
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
